<template>
  <v-row no-gutters>
    <v-col cols="12" lg="8" md="8">
      <!-- Left column -->
      <p class="text-h2">{{ $t('STEPPER_STEP3_TITLE') }}</p>
      <p class="text-h3 grey-font">{{ $t('STEPPER_STEP3_APPROVED_TITLE') }}</p>

      <!-- Approved payment methods -->
      <v-radio-group class="ml-2" v-model="selectedMethodId" :mandatory="false" hide-details>
        <v-radio
          v-for="(radio, index) in approvedMethods"
          class="pb-2"
          :key="'radio-' + index"
          :label="$t(radio.desc_key)"
          :value="radio.id"
          :readonly="true"
          :disabled="!canEditOffer && radio.id !== selectedMethodId"
        ></v-radio>
      </v-radio-group>
      <!-- End approved payment methods -->

      <!-- Non-approved forms of payment -->
      <v-btn
        v-if="!showUnapproved && unapprovedMethods.length !== 0"
        text
        color="primary"
        :ripple="false"
        @click="showUnapproved = true"
        class="link non-text-transformation display-btn"
        data-cy="button-show-unapproved"
      >
        {{ $t('STEPPER_STEP3_VIEW_MORE') }}
      </v-btn>
      <div v-if="showUnapproved" class="mt-4">
        <p class="text-h3 grey-font">
          {{ $t('STEPPER_STEP3_UNAPPROVED_TITLE') }}
        </p>
        <v-radio-group class="ml-2" v-model="selectedMethodId" hide-details :mandatory="false">
          <v-radio
            v-for="(radio, index) in unapprovedMethods"
            class="pb-2"
            :key="'radioUnapproved-' + index"
            :label="$t(radio.desc_key)"
            :value="radio.id"
            :readonly="true"
            :disabled="!canEditOffer && radio.id !== selectedMethodId"
          ></v-radio>
        </v-radio-group>
        <v-btn
          text
          color="primary"
          :ripple="false"
          @click="showUnapproved = false"
          class="link non-text-transformation display-btn"
          data-cy="button-hide-unapproved"
        >
          {{ $t('STEPPER_STEP3_VIEW_LESS') }}
        </v-btn>
      </div>
      <!-- Non-approved forms of payment end -->

      <!-- Payment day -->
      <p class="text-h3 mt-4 grey-font">
        {{ $t('STEPPER_STEP3_PAYMENT_DAY_TITLE') }}
      </p>
      <v-form @submit.prevent>
        <v-checkbox
          v-if="paymentDay === 0"
          :label="$t('STEPPER_STEP3_WITHOUT_PAYMENT_DAY')"
          :true-value="true"
          :false-value="false"
          v-model="noPaymentDay"
          :disabled="!canEditOffer && !!paymentDay"
          :readonly="true"
          @change="handleNoPaymentDayChange(noPaymentDay)"
        ></v-checkbox>
        <v-text-field
          v-else
          ref="paymentDay"
          :label="$t('STEPPER_STEP3_PAYMENT_DAY_LABEL')"
          class="ml-2 text-body-2 payment__number"
          outlined
          v-model="paymentDay"
          :disabled="noPaymentDay"
          :readonly="true"
          @focus="!canEditOffer && $event.target.blur()"
          :rules="dayRules"
          data-cy="payment-day-input"
        ></v-text-field>
      </v-form>
      <!-- Payment day end -->

      <!-- Bank guarantee -->
      <div v-if="hasBankGuarantee">
        <p class="text-h3 grey-font">
          {{ $t('STEPPER_STEP3_BANK_GUARANTEE_TITLE') }}
        </p>

        <v-text-field
          :label="$t('GUARANTEE_AMOUNT')"
          class="ml-2 text-body-2 payment__number"
          outlined
          v-model="bankGuaranteeAmount"
          :rules="bankGuaranteeAmountRules"
          :readonly="true"
          @focus="!canEditOffer && $event.target.blur()"
        ></v-text-field>

        <p class="text-h4 mb-0 grey-font">
          {{ $t('GUARANTEE_TYPE_LABEL') }}
        </p>
        <v-radio-group
          class="ml-2 mt-0"
          v-model="selectedTypeBrokerKey"
          :mandatory="false"
          hide-details
        >
          <v-radio
            v-for="radio in typesBankGuarantee"
            class="pb-2"
            :key="radio.key"
            :label="$t(radio.key)"
            :value="radio.key"
            :readonly="true"
            :disabled="!canEditOffer && radio.key !== selectedTypeBrokerKey"
          ></v-radio>
        </v-radio-group>

        <p class="text-h4 mt-4 mb-0 grey-font">
          {{ $t('GUARANTEE_DURATION_LABEL') }}
        </p>
        <v-radio-group
          class="ml-2 mt-0"
          v-model="selectedDurationsBrokerKey"
          :mandatory="false"
          hide-details
        >
          <v-radio
            v-for="radio in durationsBrokerGuarantee"
            class="pb-2"
            :key="radio.key"
            :label="$t(radio.key)"
            :value="radio.key"
            :readonly="true"
            :disabled="!canEditOffer && radio.key !== selectedDurationsBrokerKey"
          />
        </v-radio-group>
      </div>
      <!-- Bank guarantee end -->
      <!-- Left column end -->
    </v-col>

    <SummaryCard>
      <template v-slot:save-and-reset-offer-buttons>
        <SaveAndResetOfferButtons @onSaveClicked="saveOffer" @onResetClicked="resetOffer"></SaveAndResetOfferButtons>
      </template>

      <template v-slot:offer-status-button>
        <UpdateOfferStatusButton></UpdateOfferStatusButton>
      </template>
    </SummaryCard>

    <AdditionalInfoRequiredDialog :showDialog="infoRequiredDialog"></AdditionalInfoRequiredDialog>
  </v-row>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex'
import { GET_OFFER_ITEM } from '@/store/modules/offer/getters'
import {
  SET_OFFER_PAYMENT_METHOD_SELECTION,
  SET_OFFER_PAYMENT_DAY,
  SET_BANK_GUARANTEE_AMOUNT,
  SET_BANK_GUARANTEE_TYPE,
  SET_BANK_GUARANTEE_DURATION,
} from '@/store/modules/offer/mutations'
import SummaryCard from '@/components/summary-card'
import SaveAndResetOfferButtons from '@/components/buttons/SaveAndResetOfferButtons'
import UpdateOfferStatusButton from '@/components/buttons/UpdateOfferStatusButton'
import AdditionalInfoRequiredDialog from '@/containers/offer-form/payment-methods/AdditionalInfoRequiredDialog'

const DEFAULT_PAYMENT_DAY = 1
const NO_PAYMENT_DAY = 0

export default {
  name: 'PaymentMethods',
  components: {
    SummaryCard,
    AdditionalInfoRequiredDialog,
    SaveAndResetOfferButtons,
    UpdateOfferStatusButton,
  },
  data() {
    return {
      showUnapproved: false,
      warnings: false,
      noPaymentDay: true,
      dayRules: [
        v => !!v || this.noPaymentDay || this.$t('STEPPER_STEP3_DAYS_RULE_MANDATORY'),
        v => (v && v >= 1) || this.noPaymentDay || this.$t('STEPPER_STEP3_DAYS_RULE_MIN'),
        v => (v && v <= 30) || this.noPaymentDay || this.$t('STEPPER_STEP3_DAYS_RULE_MAX'),
      ],
      bankGuaranteeAmountRules: [v => v > 0 || this.$t('STEPPER_STEP3_BANK_RULE_MANDATORY')],
    }
  },
  props: {
    infoRequiredDialog: {
      type: Boolean,
    },
  },
  computed: {
    ...mapGetters('offer', [GET_OFFER_ITEM, 'canEditOffer', 'hasBankGuarantee']),
    getBankGuarantee() {
      return this[GET_OFFER_ITEM]('bankGuarantee')
    },
    durationsBrokerGuarantee() {
      return this.getBankGuarantee?.durations ?? []
    },
    typesBankGuarantee() {
      return this.getBankGuarantee?.types ?? []
    },
    paymentMethods() {
      return this[GET_OFFER_ITEM]('paymentMethods')
    },
    approvedMethods() {
      return this.paymentMethods?.filter(method => method.isDefault) ?? []
    },
    unapprovedMethods() {
      return this.paymentMethods?.filter(method => !method.isDefault) ?? []
    },
    selectedMethod() {
      return this.paymentMethods?.filter(method => method.isSelected)
    },
    selectedDurationsBroker() {
      return this.durationsBrokerGuarantee?.filter(duration => duration.isSelected)
    },
    selectedTypeBroker() {
      return this.typesBankGuarantee?.filter(type => type.isSelected)
    },
    selectedMethodId: {
      get() {
        return this.selectedMethod[0]?.id ?? ''
      },
      set(value) {
        this.changeSelectedMethod(value)
      },
    },
    paymentDay: {
      get() {
        return this[GET_OFFER_ITEM]('additionalServices').paymentDay ?? NO_PAYMENT_DAY
      },
      set(value) {
        this.changeDay(value)
      },
    },
    isDayValid() {
      return this.noPaymentDay && this.paymentDay === 0
        ? true
        : this.paymentDay !== '' && this.paymentDay >= 1 && this.paymentDay <= 30
    },
    isPaymentMethodSelected() {
      return this.paymentMethods?.some(method => method.isSelected)
    },
    hasRequiredPaymentData() {
      return this.isDayValid && this.isPaymentMethodSelected
    },
    bankGuaranteeAmount: {
      get() {
        return this.getBankGuarantee?.amount
      },
      set(value) {
        this.updateBankGuaranteeAmount(value)
      },
    },
    selectedTypeBrokerKey: {
      get() {
        return this.selectedTypeBroker[0].key ?? ''
      },
      set(value) {
        this.updateBankGuaranteeType(value)
      },
    },
    selectedDurationsBrokerKey: {
      get() {
        return this.selectedDurationsBroker[0].key ?? ''
      },
      set(value) {
        this.updateBankGuaranteeDuration(value)
      },
    },
  },
  methods: {
    ...mapMutations('offer', [
      SET_OFFER_PAYMENT_METHOD_SELECTION,
      SET_OFFER_PAYMENT_DAY,
      SET_BANK_GUARANTEE_AMOUNT,
      SET_BANK_GUARANTEE_TYPE,
      SET_BANK_GUARANTEE_DURATION,
    ]),
    ...mapMutations('process', ['setIsPaymentFormValid']),
    changeSelectedMethod(id) {
      this[SET_OFFER_PAYMENT_METHOD_SELECTION]({ id })
    },
    changeDay(day) {
      this[SET_OFFER_PAYMENT_DAY]({ day: Number(day) })
    },
    changeDayRECD() {
      const DAY_RECD = 15
      this[SET_OFFER_PAYMENT_DAY]({ day: Number(DAY_RECD) })
      return DAY_RECD
    },
    handleNoPaymentDayChange(checked) {
      const value = checked ? NO_PAYMENT_DAY : DEFAULT_PAYMENT_DAY
      this[SET_OFFER_PAYMENT_DAY]({ day: Number(value) })
      this.$refs.paymentDay.validate()
    },
    saveOffer() {
      this.$emit('onSaveClicked')
    },
    resetOffer() {
      this.$emit('onResetClicked')
    },
    updateBankGuaranteeAmount(value) {
      const numberValue = Number(value)
      const validValue = isNaN(numberValue) ? 0 : numberValue
      this[SET_BANK_GUARANTEE_AMOUNT](validValue)
    },
    updateBankGuaranteeType(value) {
      this[SET_BANK_GUARANTEE_TYPE](value)
    },
    updateBankGuaranteeDuration(value) {
      this[SET_BANK_GUARANTEE_DURATION](value)
    },
    formValidationChangeHandler() {
      this.$emit('onChangeIsContinueForbidden', !this.hasRequiredPaymentData)
      this.setIsPaymentFormValid(this.hasRequiredPaymentData)
    },
  },
  created() {
    this.formValidationChangeHandler()
    this.noPaymentDay = this.paymentDay === NO_PAYMENT_DAY
    if (this.unapprovedMethods.map(element => element.id).includes(this.selectedMethodId)) {
      this.showUnapproved = true
    }
  },
  watch: {
    paymentDay() {
      this.noPaymentDay = this.paymentDay === NO_PAYMENT_DAY
    },
    hasRequiredPaymentData() {
      this.formValidationChangeHandler()
    },
  },
}
</script>

<style scoped lang="scss">
.payment {
  &__number {
    max-width: rem(270);
  }
}

.cursor-pointer {
  cursor: pointer;
}
</style>
